import { BellOutlined } from "@ant-design/icons";
import { Badge, Button, Divider, Flex, Popover, Skeleton, Typography } from "antd";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { actions } from "redux-router5";
import { resolveNotificationTarget } from "../../common/notifications/notificationTarget";
import {
  getNotificationTypeConfig,
  resolveIconColors,
} from "../../common/notifications/notificationTypeConfig";
import { formatRelativeTime } from "../../common/utils/formatRelativeTime";
import type { NotificationItem } from "../../common/utils/useNotifications";
import { useNotifications } from "../../common/utils/useNotifications";
import { routeNames } from "../common/routes";
import type { AppDispatch } from "../store";
import { managementNotificationsAdapter } from "./managementNotificationsAdapter";
import styles from "./NotificationBell.module.css";

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead } =
    useNotifications(managementNotificationsAdapter);

  const handleItemClick = (notification: NotificationItem) => {
    const { id } = notification;
    if (id && !notifications.find((n) => n.id === id)?.read_at) {
      markAsRead(id);
    }
    const target = resolveNotificationTarget(notification);
    if (target) {
      dispatch(actions.navigateTo(routeNames.TICKET_VIEW, target));
    }
    setOpen(false);
  };

  const panelContent = (
    <div className={styles.panel}>
      <Flex align="center" justify="space-between" className={styles.panelHeader}>
        <Typography.Text strong>Notifications</Typography.Text>
        <Button
          type="link"
          size="small"
          disabled={unreadCount === 0}
          onClick={markAllAsRead}
        >
          Mark all as read
        </Button>
      </Flex>
      <Divider className={styles.headerDivider} />

      {loading && (
        <div className={styles.listContainer}>
          {[1, 2, 3].map((key) => (
            <div key={key} className={styles.skeletonItem}>
              <Skeleton avatar={{ size: 32 }} paragraph={{ rows: 1 }} active />
            </div>
          ))}
        </div>
      )}

      {!loading && error && (
        <div className={styles.emptyState}>
          <Typography.Text type="secondary">Failed to load notifications</Typography.Text>
        </div>
      )}

      {!loading && !error && notifications.length === 0 && (
        <div className={styles.emptyState}>
          <BellOutlined className={styles.emptyIcon} />
          <Typography.Text type="secondary">No notifications yet</Typography.Text>
        </div>
      )}

      {!loading && !error && notifications.length > 0 && (
        <div className={styles.listContainer}>
          {notifications.map((notification) => {
            const typeConfig = getNotificationTypeConfig(notification.notification_type);
            const colors = resolveIconColors(typeConfig.color);
            const isUnread =
              notification.read_at === null || notification.read_at === undefined;

            return (
              <button
                key={notification.id}
                type="button"
                className={`${styles.notifItem} ${isUnread ? styles.notifItemUnread : ""}`}
                onClick={() => handleItemClick(notification)}
              >
                <div
                  className={styles.iconCircle}
                  style={{ background: colors.bg, color: colors.fg }}
                >
                  {typeConfig.icon}
                </div>
                <div className={styles.notifBody}>
                  <Typography.Text
                    strong={isUnread}
                    ellipsis={{ tooltip: notification.message }}
                    className={styles.notifMessage}
                  >
                    {notification.message}
                  </Typography.Text>
                  <Typography.Text type="secondary" className={styles.notifTime}>
                    {notification.created_at
                      ? formatRelativeTime(notification.created_at)
                      : ""}
                  </Typography.Text>
                </div>
                {isUnread && <div className={styles.unreadDot} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={panelContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayClassName={styles.popoverOverlay}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]}>
        <Button
          type="text"
          icon={<BellOutlined />}
          className={styles.bellButton}
          aria-label="Notifications"
        />
      </Badge>
    </Popover>
  );
}
